import axios from 'axios';
import { getApiEndpoint, fileUpload } from '@/utils/ERPUtils';

import { logger } from '@/utils/logger';
/**
 * ERPNext API Response Wrapper
 * All ERPNext API endpoints return responses in this format
 */
export interface ERPApiResponse<T = any> {
  message: {
    status: 'success' | 'error';
    message: string;
    data: T;
  };
}

/**
 * Company Data Structure (ERPNext DocType)
 */
export interface Company {
  name: string; // Company ID (e.g., "COMP-00001")
  organization_name: string; // Organization name
  domain?: string;
  contact_number?: string;
  email?: string;
  company_logo?: string; // Logo

  // Standard ERPNext fields
  creation?: string;
  modified?: string;
  owner?: string;
}

/**
 * Update Company Request Data
 */
export interface UpdateCompanyRequest {
  organization_name?: string;
  domain?: string;
  contact_number?: string;
  email?: string;
}

/**
 * Company Service
 * Handles all company branding operations with ERPNext backend
 */
class CompanyService {
  private cache: Map<string, { data: any; timestamp: number; expiry: number }> = new Map();
  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Generic cached API call method
   */
  private async cachedApiCall<T>(
    cacheKey: string,
    apiCall: () => Promise<T>,
    cacheDuration = this.DEFAULT_CACHE_DURATION
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      return cached.data;
    }

    try {
      const result = await apiCall();

      // Cache the results
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
        expiry: Date.now() + cacheDuration
      });

      return result;
    } catch (error) {
      logger.error(`API call failed for ${cacheKey}:`, error);
      throw error;
    }
  }

  /**
   * Get company details by ID
   *
   * @param companyId - Company document name (e.g., "COMP-00001")
   * @returns Complete company document with branding settings
   */
  async getCompanyDetails(companyId: string): Promise<Company> {
    const cacheKey = `company_${companyId}`;

    return this.cachedApiCall(cacheKey, async () => {
      const response = await axios.post<ERPApiResponse<Company>>(
        getApiEndpoint('GET_COMPANY_DETAILS'),
        { company_id: companyId },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.message.status === 'success') {
        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message);
      }
    });
  }

  /**
   * Update company details and branding
   *
   * @param companyId - Company document name
   * @param data - Fields to update
   * @returns Updated company data
   */
  async updateCompanyDetails(
    companyId: string,
    data: Partial<Company>
  ): Promise<Company> {
    try {
      const response = await axios.post<ERPApiResponse<Company>>(
        getApiEndpoint('UPDATE_COMPANY_DETAILS'),
        { company_id: companyId, ...data },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.message.status === 'success') {
        // Clear company cache after successful update
        this.clearCacheEntry(`company_${companyId}`);

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message);
      }
    } catch (error) {
      logger.error('Update company details failed:', error);

      // Provide more descriptive error messages
      if (axios.isAxiosError(error)) {
        const errorMsg = error.response?.data?.message?.message ||
                        error.response?.data?.message ||
                        'Failed to update company details';
        throw new Error(errorMsg);
      }

      throw error;
    }
  }

  /**
   * Upload company logo
   *
   * @param companyId - Company document name
   * @param file - Logo image file or base64 string to upload
   * @returns Logo URL path
   */
  async uploadCompanyLogo(companyId: string, file: File | string): Promise<string> {
    try {
      let fileToUpload: File;

      // Step 1: Convert base64 string to File if needed
      if (typeof file === 'string') {
        // Base64 string - convert to File
        const base64Data = file.split(',')[1] || file;
        const mimeType = file.match(/data:([^;]+);/)?.[1] || 'image/png';
        const byteString = atob(base64Data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uint8Array], { type: mimeType });
        const fileName = `company-logo-${Date.now()}.${mimeType.split('/')[1]}`;
        fileToUpload = new File([blob], fileName, { type: mimeType });
      } else {
        fileToUpload = file;
      }

      // Step 2: Upload file using ERPUtils fileUpload function
      const uploadResult = await fileUpload(fileToUpload, 5); // 5MB max size

      if (uploadResult.error) {
        throw new Error(uploadResult.error);
      }

      if (!uploadResult.url) {
        throw new Error('File upload did not return a URL');
      }

      // Step 3: Update company with new logo URL
      const response = await axios.post<ERPApiResponse<{ logo_url: string }>>(
        getApiEndpoint('UPLOAD_COMPANY_LOGO'),
        {
          company_id: companyId,
          logo_url: uploadResult.url
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.message.status === 'success') {
        // Clear company cache after logo update
        this.clearCacheEntry(`company_${companyId}`);

        return response.data.message.data.logo_url;
      } else {
        throw new Error(response.data.message.message);
      }
    } catch (error) {
      logger.error('Upload company logo failed:', error);

      // Provide more descriptive error messages
      if (axios.isAxiosError(error)) {
        const errorMsg = error.response?.data?.message?.message ||
                        error.response?.data?.message ||
                        'Failed to upload company logo';
        throw new Error(errorMsg);
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Failed to upload company logo');
    }
  }

  // Cache management methods
  clearCache(): void {
    this.cache.clear();
  }

  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }

  clearCacheByPattern(pattern: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(pattern));
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  getCacheStatus() {
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      timestamp: value.timestamp,
      expiry: value.expiry,
      isExpired: Date.now() >= value.expiry,
      timeUntilExpiry: Math.max(0, value.expiry - Date.now())
    }));

    return {
      totalEntries: this.cache.size,
      entries
    };
  }
}

// Export singleton instance
export const companyService = new CompanyService();
